/* eslint-disable no-param-reassign */
import { ApplianceType } from '@alpinehomeair/common';
import { createAsyncThunk, Dispatch, UnknownAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import {
  AboveGradeWallType,
  AttributeType,
  BelowGradeWallType,
  DoorItemType,
  ExposureDirectionType,
  FootprintPolygonsType,
  GlassItemType,
  LevelType,
  LevelTypeType,
  PositionAndDimensionsType,
  PositionType,
  SectionType,
  SkylightItemType,
  SpaceType
} from 'data/types';
import { setSpacesLoading } from 'features/ai/ai-slice';
import { updateProjectSetting } from 'features/project';
import { produce } from 'immer';
import { nanoid } from 'nanoid';
import axios from 'utils/api';
import {
  countFireplaceAppliances,
  createFireplaceAppliance,
  findFireplaceTargetSpaceId
} from 'utils/appliances';
import {
  applyLeftoverCarvePlans,
  applyLevelLeftoverRebuild,
  HYBRID_LEFTOVER_HOLE_MESSAGE,
  incomingSpacePatch,
  planLeftoverCarves
} from 'utils/apply-carve-leftover';
import { collectLevelAutoLayoutSpaces } from 'utils/auto-layout-space';
import {
  autoGenerateFullDuctworkLevelSpaces,
  autoGenerateHybridFullLevelSpaces,
  autoMirrorUnconditionedLevelOpenSpaces,
  collectNewSpaceIds
} from 'utils/auto-mirror-level-layout';
import { refetchStructureSpaces } from 'utils/data';
import { exposureForEdgeRun } from 'utils/edge-exposure';
import { getOrientationDelta } from 'utils/exposure-rotation';
import {
  clampRectToFootprints,
  findContainingPolygon,
  findPolygonForPoints,
  getEffectiveFootprintPolygons
} from 'utils/footprint-polygons';
import { getLevelsReceivingMirroredFootprint } from 'utils/footprint-preset-area';
import { scaleFootprintPoints } from 'utils/footprint-scaling';
import { payloadFromEditedShapes } from 'utils/footprint-shapes-commit';
import { FootprintCommitPayload } from 'utils/footprint-shapes-commit-types';
import {
  cloneFootprintShapes,
  getEffectiveFootprintShapes
} from 'utils/footprint-union';
import { leftoverRebuildTargetsForLevel } from 'utils/leftover-space-targets';
import { computeOpenToAfterReshape } from 'utils/open-to';
import {
  openingPresetForDoor,
  openingPresetForWindowType
} from 'utils/opening-drag';
import { runBoundsForWall } from 'utils/opening-layout';
import { normalizeOpeningOnWall } from 'utils/opening-position';
import { withOpeningPosition } from 'utils/opening-position-value';
import {
  EdgeRunType,
  OpeningRunBoundsType
} from 'utils/opening-position-types';
import { OpeningRunLayoutType } from 'utils/opening-run-layout-types';
import { findOpeningRunLayout } from 'utils/opening-run-wall';
import {
  DesiredPolygonWall,
  wallWidthForLengthFt
} from 'utils/polygon-exterior-walls';
import { snapPolygonVerticesToGrid } from 'utils/polygon-grid-snap';
import { getProjectSettingValue, isHybridOnlyPreference } from 'utils/project';
import { normalizeSolidDoorAttributes } from 'utils/sizing';
import {
  openingRunBoundsForSpaceEdge,
  openingRunsForSpace
} from 'utils/space-opening-run';
import {
  assignSpacePolygonShape,
  AutoExtendedEdge,
  boundingBoxOfVertices,
  buildSpaceShapeWithFootprintExtensionFromPolygons,
  EdgeCardinal,
  extendEdgeToFootprint,
  extendPolygonShapeToFootprint,
  extendRectAsPolygonToFootprint,
  getSpaceOverlapRect,
  getSpaceShapeLayout,
  getSpaceVertices,
  hasNearbyAngledFootprintSegment,
  isPolygonInsideAnyFootprint,
  isPolygonInsideFootprint,
  isRectangleShape,
  polygonArea,
  polygonOverlapsAny,
  RelativeVertex,
  SpaceShapeId
} from 'utils/space-polygon';
import {
  computeSpaceScaledSquareFootage,
  scaleSpaceGeometry
} from 'utils/space-scaling';
import { fitShapeInOpenArea } from 'utils/space-shape-fit';
import {
  reconcileWallsForGeometry,
  redistributeWallItems
} from 'utils/space-wall-reconcile';
import {
  createLevel,
  createSection,
  createSpace,
  findLevelBySpaceId,
  fitSpaceAmongOthers,
  getAssignedImageForLevel,
  getOrientedEdge,
  isDetachedSpaceItem,
  isLevelConditioned
} from 'utils/structure';
import { structureEditResetsDesigns } from 'utils/structure-edit-reset';
import { FoundationTypeType, getFoundationType } from 'utils/structure-basics';
import {
  isTriangleShape,
  remapExtensionRecordsAfterSnap,
  snapTriangleVerticesToGrid
} from 'utils/triangle-grid-snap';
import {
  confirmationModal,
  errorModal,
  notification,
  warningModal
} from 'utils/ui';
import { openNeighborWallRunBounds } from 'utils/wall-run-opening-bounds';
import { beginUndoBatch, endUndoBatch } from './structure-history-middleware';
import { reconcileSpaceWallsRequested } from './structure-reconcile-actions';
import {
  addAutoLayoutSpaceIds,
  deleteStructureItem,
  normalizeLevelOrder,
  rotateAllWallExposures,
  setLevelFootprint,
  setSpaceExtensionRecords,
  setStructure,
  setSuggestedSpaces,
  updateStructureItem,
  updateStructureItemAttribute
} from './structure-slice';
import {
  SpaceWallReconcileRequestType,
  SpaceWallsUpdateType
} from './structure-reconcile-types';
import { wallsContentEqual } from './structure-thunks-helpers';

let currentGenerateStructureController: AbortController | null = null;

type StructureIndices = {
  levelIndex: number;
  sectionIndex: number;
  spaceIndex: number;
};

function findLevelIndexBySpaceId(
  structure: LevelType[],
  spaceId: string
): number {
  return structure.findIndex((l) =>
    l.sections.some((s) => s.spaces.some((ss) => ss.uniqueId === spaceId))
  );
}

function findLevelIndexBySectionId(
  structure: LevelType[],
  sectionId: string
): number {
  return structure.findIndex((l) =>
    l.sections.some((s) => s.uniqueId === sectionId)
  );
}

function findSectionIndexBySpaceId(
  structure: LevelType[],
  levelIndex: number,
  spaceId: string
): number {
  return structure[levelIndex].sections.findIndex((s) =>
    s.spaces.some((ss) => ss.uniqueId === spaceId)
  );
}

function findSpaceIndices(
  structure: LevelType[],
  spaceId: string,
  targetSectionId: string
): {
  from: StructureIndices;
  toLevelIndex: number;
  toSectionIndex: number;
} | null {
  const fromLevelIndex = findLevelIndexBySpaceId(structure, spaceId);
  const toLevelIndex = findLevelIndexBySectionId(structure, targetSectionId);

  if (fromLevelIndex === -1 || toLevelIndex === -1) {
    return null;
  }

  const fromSectionIndex = findSectionIndexBySpaceId(
    structure,
    fromLevelIndex,
    spaceId
  );
  const toSectionIndex = structure[toLevelIndex].sections.findIndex(
    (s) => s.uniqueId === targetSectionId
  );

  if (fromSectionIndex === -1 || toSectionIndex === -1) {
    return null;
  }

  const fromSpaceIndex = structure[fromLevelIndex].sections[
    fromSectionIndex
  ].spaces.findIndex((s) => s.uniqueId === spaceId);

  if (fromSpaceIndex === -1) {
    return null;
  }

  return {
    from: {
      levelIndex: fromLevelIndex,
      sectionIndex: fromSectionIndex,
      spaceIndex: fromSpaceIndex
    },
    toLevelIndex,
    toSectionIndex
  };
}

type SpaceLocation = {
  space: SpaceType;
  levelIndex: number;
  sectionIndex: number;
  spaceIndex: number;
};

type EdgeType = 'top' | 'right' | 'bottom' | 'left';

const EDGE_TO_EXPOSURE: Record<EdgeType, ExposureDirectionType> = {
  top: 'North',
  right: 'East',
  bottom: 'South',
  left: 'West'
};

const ALL_EDGES: EdgeType[] = ['top', 'right', 'bottom', 'left'];

function findSpaceLocation(
  structure: LevelType[],
  spaceId: string
): SpaceLocation | null {
  for (let levelIndex = 0; levelIndex < structure.length; levelIndex += 1) {
    const level = structure[levelIndex];
    for (
      let sectionIndex = 0;
      sectionIndex < level.sections.length;
      sectionIndex += 1
    ) {
      const section = level.sections[sectionIndex];
      for (
        let spaceIndex = 0;
        spaceIndex < section.spaces.length;
        spaceIndex += 1
      ) {
        const space = section.spaces[spaceIndex];
        if (space.uniqueId === spaceId) {
          return { space, levelIndex, sectionIndex, spaceIndex };
        }
      }
    }
  }
  return null;
}

function ensureSizingAttributes(space: SpaceType): void {
  if (!space.sizingAttributes) {
    space.sizingAttributes = {
      aboveGradeWalls: [],
      belowGradeWalls: [],
      ceilingsBelowRoof: [],
      ceilingsBelowAttic: [],
      floorsOverUnconditionedSpace: [],
      floorsOverExposedSpace: [],
      basementFloors: [],
      slabFloors: [],
      appliances: []
    };
  }
}

function getSpaceDimensions(space: SpaceType): {
  squareFootage: number;
  ceilingHeight: number;
} {
  const squareFootageAttr = space.attributes.find(
    (attr) => attr.code === 'square_footage'
  );
  const ceilingHeightAttr = space.attributes.find(
    (attr) => attr.code === 'ceiling_height'
  );

  let squareFootage = 0;

  if (space.width && space.length && space.width > 0 && space.length > 0) {
    squareFootage = space.width * space.length;
  }

  if (squareFootage === 0 && squareFootageAttr?.value) {
    squareFootage = parseFloat(squareFootageAttr.value) || 0;
  }

  if (squareFootage === 0) {
    squareFootage = 100;
  }

  return {
    squareFootage,
    ceilingHeight: parseFloat(ceilingHeightAttr?.value) || 8
  };
}

function createDefaultWall(
  defaultSizingItems: RootState['data']['defaultSizingItems'],
  edge: EdgeType,
  exposure: ExposureDirectionType,
  ceilingHeight: number,
  squareFootage: number,
  isBasement = false
): AboveGradeWallType | BelowGradeWallType {
  if (isBasement) {
    return {
      ...defaultSizingItems.belowGradeWall,
      uniqueId: nanoid(5),
      valid: true,
      height: ceilingHeight,
      width: Math.ceil(Math.sqrt(squareFootage)),
      exposure,
      verticalGlass: [],
      solidDoors: []
    } as BelowGradeWallType;
  }

  return {
    ...defaultSizingItems.aboveGradeWall,
    uniqueId: nanoid(5),
    valid: true,
    height: ceilingHeight,
    width: Math.ceil(Math.sqrt(squareFootage)),
    edge,
    exposure,
    verticalGlass: [],
    solidDoors: []
  } as AboveGradeWallType;
}

function findOrCreateWallForRunLayout(
  space: SpaceType,
  layout: OpeningRunLayoutType,
  exposure: ExposureDirectionType,
  defaultSizingItems: RootState['data']['defaultSizingItems'],
  isBasement = false
): AboveGradeWallType | BelowGradeWallType {
  ensureSizingAttributes(space);

  const existingWall = layout.wallUniqueId
    ? [
        ...space.sizingAttributes!.aboveGradeWalls,
        ...space.sizingAttributes!.belowGradeWalls
      ].find((wall) => wall.uniqueId === layout.wallUniqueId)
    : undefined;
  if (existingWall) {
    return existingWall;
  }

  const { ceilingHeight } = getSpaceDimensions(space);
  const width = wallWidthForLengthFt(layout.run.lengthFt);
  const newWall = createDefaultWall(
    defaultSizingItems,
    layout.run.edge,
    exposure,
    ceilingHeight,
    width * width,
    isBasement
  );
  newWall.width = width;
  if (isBasement) {
    space.sizingAttributes!.belowGradeWalls.push(newWall as BelowGradeWallType);
  } else {
    space.sizingAttributes!.aboveGradeWalls.push(newWall as AboveGradeWallType);
  }
  return newWall;
}

function findOrCreateWall(
  space: SpaceType,
  edge: EdgeType,
  exposure: ExposureDirectionType,
  defaultSizingItems: RootState['data']['defaultSizingItems'],
  isBasement = false
): AboveGradeWallType | BelowGradeWallType {
  ensureSizingAttributes(space);

  if (isBasement) {
    let targetWall: AboveGradeWallType | BelowGradeWallType | undefined =
      space.sizingAttributes!.belowGradeWalls.find(
        (wall) => wall.exposure === exposure
      );

    if (!targetWall) {
      targetWall = space.sizingAttributes!.aboveGradeWalls.find(
        (wall) => wall.edge === edge
      );
    }

    if (!targetWall) {
      targetWall = space.sizingAttributes!.aboveGradeWalls.find(
        (wall) => !wall.edge && wall.exposure === exposure
      );
    }

    if (!targetWall) {
      const { squareFootage, ceilingHeight } = getSpaceDimensions(space);
      targetWall = createDefaultWall(
        defaultSizingItems,
        edge,
        exposure,
        ceilingHeight,
        squareFootage,
        true
      ) as BelowGradeWallType;
      space.sizingAttributes!.belowGradeWalls.push(targetWall);
    }

    return targetWall;
  }

  let targetWall = space.sizingAttributes!.aboveGradeWalls.find(
    (wall) => wall.edge === edge
  );

  if (!targetWall) {
    targetWall = space.sizingAttributes!.aboveGradeWalls.find(
      (wall) => !wall.edge && wall.exposure === exposure
    );
  }

  if (!targetWall) {
    const { squareFootage, ceilingHeight } = getSpaceDimensions(space);
    targetWall = createDefaultWall(
      defaultSizingItems,
      edge,
      exposure,
      ceilingHeight,
      squareFootage
    ) as AboveGradeWallType;
    space.sizingAttributes!.aboveGradeWalls.push(targetWall);
  } else if (!targetWall.edge) {
    targetWall.edge = edge;
    targetWall.exposure = exposure;
  }

  return targetWall;
}

type AdjacencyMap = Record<EdgeType, SpaceType | null>;

const ALL_DIRECTIONS: ExposureDirectionType[] = [
  'North',
  'North East',
  'East',
  'South East',
  'South',
  'South West',
  'West',
  'North West'
];

const EDGE_TO_DIRECTION_INDEX: Record<EdgeType, number> = {
  top: 0,
  right: 2,
  bottom: 4,
  left: 6
};

function getOrientedExposure(
  edge: EdgeType,
  homeOrientation: ExposureDirectionType | undefined
): ExposureDirectionType {
  if (!homeOrientation || homeOrientation === 'North') {
    return EDGE_TO_EXPOSURE[edge];
  }

  const baseIndex = EDGE_TO_DIRECTION_INDEX[edge];
  const orientationIndex = ALL_DIRECTIONS.indexOf(homeOrientation);

  if (orientationIndex === -1) {
    return EDGE_TO_EXPOSURE[edge];
  }

  const rotatedIndex = (baseIndex - orientationIndex + 8) % 8;
  return ALL_DIRECTIONS[rotatedIndex];
}

function calculateAdjacency(
  space: SpaceType,
  otherSpaces: SpaceType[]
): AdjacencyMap {
  const adjacency: AdjacencyMap = {
    top: null,
    right: null,
    bottom: null,
    left: null
  };

  if (!space.position || !space.width || !space.length) {
    return adjacency;
  }

  const sp: PositionAndDimensionsType = {
    ...space.position,
    width: space.width,
    height: space.length
  };

  otherSpaces.forEach((other) => {
    if (!other.position || !other.width || !other.length) {
      return;
    }

    const op: PositionAndDimensionsType = {
      ...other.position,
      width: other.width,
      height: other.length
    };

    if (sp.x < op.x + op.width && sp.x + sp.width > op.x) {
      if (sp.y === op.y + op.height) {
        adjacency.top = other;
      }
      if (sp.y + sp.height === op.y) {
        adjacency.bottom = other;
      }
    }

    if (sp.y < op.y + op.height && sp.y + sp.height > op.y) {
      if (sp.x === op.x + op.width) {
        adjacency.left = other;
      }
      if (sp.x + sp.width === op.x) {
        adjacency.right = other;
      }
    }
  });

  return adjacency;
}

function getWallCardinal(
  wall: AboveGradeWallType | BelowGradeWallType,
  homeOrientation: ExposureDirectionType | undefined
): EdgeCardinal | undefined {
  if ('edge' in wall && wall.edge) return wall.edge as EdgeCardinal;
  return getOrientedEdge(wall.exposure, homeOrientation) as
    | EdgeCardinal
    | undefined;
}

function shouldRemoveWallBetweenSpaces(
  currentSpace: SpaceType,
  adjacentSpace: SpaceType | null
): boolean {
  if (!adjacentSpace) {
    return false;
  }
  return currentSpace.openTo?.includes(adjacentSpace.uniqueId) ?? false;
}

function calculateWallWidth(space: SpaceType, edge: EdgeType): number {
  if (!space.width || !space.length) {
    return 10;
  }
  if (edge === 'top' || edge === 'bottom') {
    return Math.max(1, Math.round(space.width));
  }
  return Math.max(1, Math.round(space.length));
}

function updateWallsBasedOnAdjacency(
  space: SpaceType,
  adjacency: AdjacencyMap,
  defaultSizingItems: RootState['data']['defaultSizingItems'],
  homeOrientation?: ExposureDirectionType,
  isBasement = false,
  level?: LevelType
): void {
  if (!space.sizingAttributes) {
    return;
  }

  if (space.conditioned === false) {
    space.sizingAttributes.aboveGradeWalls = [];
    space.sizingAttributes.belowGradeWalls = [];
    return;
  }

  const { ceilingHeight } = getSpaceDimensions(space);
  const itemsToRedistribute: {
    windows: GlassItemType[];
    doors: DoorItemType[];
  } = { windows: [], doors: [] };
  const availableWalls: (AboveGradeWallType | BelowGradeWallType)[] = [];

  ALL_EDGES.forEach((edge) => {
    const exposure = getOrientedExposure(edge, homeOrientation);

    let existingWall: AboveGradeWallType | BelowGradeWallType | undefined =
      space.sizingAttributes!.belowGradeWalls.find(
        (w) => w.exposure === exposure
      );

    if (!existingWall) {
      existingWall = space.sizingAttributes!.aboveGradeWalls.find(
        (w) => w.edge === edge
      );
    }

    if (!existingWall) {
      existingWall = space.sizingAttributes!.aboveGradeWalls.find(
        (w) => !w.edge && w.exposure === exposure
      );
    }

    const adjacentSpace = adjacency[edge];
    const shouldRemoveWall = shouldRemoveWallBetweenSpaces(
      space,
      adjacentSpace
    );

    if (adjacentSpace && shouldRemoveWall) {
      space.sizingAttributes!.aboveGradeWalls =
        space.sizingAttributes!.aboveGradeWalls.filter((w) => {
          const matches =
            w.edge === edge || (!w.edge && w.exposure === exposure);
          if (!matches) {
            return true;
          }
          if (w.verticalGlass?.length) {
            itemsToRedistribute.windows.push(...w.verticalGlass);
          }
          if (w.solidDoors?.length) {
            itemsToRedistribute.doors.push(...w.solidDoors);
          }
          return false;
        });
      space.sizingAttributes!.belowGradeWalls =
        space.sizingAttributes!.belowGradeWalls.filter((w) => {
          if (w.exposure !== exposure) {
            return true;
          }
          if (w.verticalGlass?.length) {
            itemsToRedistribute.windows.push(...w.verticalGlass);
          }
          if (w.solidDoors?.length) {
            itemsToRedistribute.doors.push(...w.solidDoors);
          }
          return false;
        });
    } else {
      const wallWidth = calculateWallWidth(space, edge);
      if (!existingWall) {
        const newWall = createDefaultWall(
          defaultSizingItems,
          edge,
          exposure,
          ceilingHeight,
          wallWidth * wallWidth,
          isBasement
        );
        newWall.width = wallWidth;
        if (isBasement) {
          space.sizingAttributes!.belowGradeWalls.push(
            newWall as BelowGradeWallType
          );
        } else {
          space.sizingAttributes!.aboveGradeWalls.push(
            newWall as AboveGradeWallType
          );
        }
        availableWalls.push(newWall);
      } else {
        if (!isBasement && !(existingWall as AboveGradeWallType).edge) {
          (existingWall as AboveGradeWallType).edge = edge;
        }
        if (!existingWall.modified) {
          existingWall.width = wallWidth;
          existingWall.exposure = exposure;
        }
        availableWalls.push(existingWall);
      }
    }
  });

  const runs = openingRunsForSpace(space, level, homeOrientation);
  redistributeWallItems(itemsToRedistribute, availableWalls, (wall) =>
    runBoundsForWall(runs, wall, homeOrientation)
  );
}

function updateSpaceAboveBelowAttributes(
  attributes: AttributeType[],
  hasSpaceAbove: boolean,
  hasSpaceBelow: boolean
): void {
  attributes.forEach((att) => {
    if (att.code === 'space_above' && !att.modified) {
      const newValue = hasSpaceAbove ? 'Yes' : '';
      if (att.value !== newValue) {
        att.value = newValue;
      }
    }
    if (att.code === 'space_below' && !att.modified) {
      const newValue = hasSpaceBelow ? 'Yes' : '';
      if (att.value !== newValue) {
        att.value = newValue;
      }
    }
  });
}

function updateShareAirAttribute(
  space: SpaceType,
  hasMultipleSpaces: boolean
): void {
  const shareAirAttribute = space.attributes.find(
    (att) => att.code === 'share_air'
  );
  if (!shareAirAttribute || shareAirAttribute.modified) {
    return;
  }
  const desiredValue = hasMultipleSpaces ? 'Open Floor Plan' : 'Not Sharing';
  if (shareAirAttribute.value !== desiredValue) {
    shareAirAttribute.value = desiredValue;
  }
}

function spaceHasRequiredAttributes(space: SpaceType): boolean {
  return (
    space.attributes.some((att) => att.code === 'square_footage') &&
    space.attributes.some((att) => att.code === 'ceiling_height')
  );
}

const abortCurrentAIRequest = () => {
  if (currentGenerateStructureController) {
    currentGenerateStructureController.abort();
    currentGenerateStructureController = null;
  }
};

export const updateStructurePositionalAttributes = createAsyncThunk(
  'structure/updateStructurePositionalAttributes',
  async (_, { getState, dispatch }) => {
    abortCurrentAIRequest();
    const state = getState() as RootState;
    const { structure } = state.structure;

    const newStructure = produce(structure, (draft) => {
      let levelCount = draft.filter((l) => l.type === 'level').length;

      draft.forEach((level, levelIndex) => {
        const hasSpaceAbove =
          levelIndex > 0 &&
          ['attic', 'crawlspace'].includes(draft[levelIndex - 1].type);
        const hasSpaceBelow =
          levelIndex < draft.length - 1 &&
          ['basement', 'crawlspace'].includes(draft[levelIndex + 1].type);

        level.sections.forEach((section) => {
          const hasMultipleSpaces = section.spaces.length > 1;

          section.spaces.filter(spaceHasRequiredAttributes).forEach((space) => {
            updateSpaceAboveBelowAttributes(
              space.attributes,
              hasSpaceAbove,
              hasSpaceBelow
            );
          });

          section.spaces.forEach((space) => {
            updateShareAirAttribute(space, hasMultipleSpaces);
          });
        });

        if (level.type === 'level' && !level.modified) {
          levelCount -= 1;
          level.label = `Floor ${levelCount + 1}`;
        }
      });
    });

    dispatch(setStructure(newStructure));
  }
);

function applyInheritableSettings(
  attributes: AttributeType[],
  projectSettings: AttributeType[]
): void {
  attributes.forEach((att) => {
    const setting = projectSettings.find((s) => s.id === att.id);
    if (setting?.inheritable) {
      att.value = setting.value;
    }
  });
}

export const updateStructureInheritableAttributeValues = createAsyncThunk(
  'structure/updateStructureInheritableAttributeValues',
  async (_, { getState, dispatch }) => {
    abortCurrentAIRequest();
    const state = getState() as RootState;
    const { structure } = state.structure;
    const { projectSettings } = state.project;

    const newStructure = produce(structure, (draft) => {
      draft.forEach((level) => {
        if (!level.modified) {
          applyInheritableSettings(level.attributes, projectSettings);
        }
        level.sections.forEach((section) => {
          section.spaces.forEach((space) => {
            if (!space.modified) {
              applyInheritableSettings(space.attributes, projectSettings);
            }
          });
        });
      });
    });

    dispatch(setStructure(newStructure));
  }
);

export const setStructureOrientation = createAsyncThunk<
  void,
  { oldOrientation: string; newOrientation: string }
>(
  'structure/setStructureOrientation',
  async ({ oldOrientation, newOrientation }, { getState, dispatch }) => {
    const delta = getOrientationDelta(oldOrientation, newOrientation);
    if (delta === undefined || delta === 0) return;

    const state = getState() as RootState;
    const attributeDefinition = state.data.attributes.find(
      (a) => a.code === 'structure_orientation'
    );

    dispatch(
      updateProjectSetting({
        attributeCode: 'structure_orientation',
        value: newOrientation,
        ...(attributeDefinition && { attributeDefinition })
      })
    );

    dispatch(rotateAllWallExposures({ delta }));
  }
);

export const addNewLevelToStructure = createAsyncThunk<
  LevelType | null,
  { levelType: LevelTypeType; sections?: SectionType[] }
>(
  'structure/addLevelToStructure',
  async ({ levelType, sections }, { getState, dispatch }) => {
    abortCurrentAIRequest();
    const state = getState() as RootState;
    const { projectSettings } = state.project;
    const { structure } = state.structure;

    const newLevel = createLevel(levelType, {
      data: {
        sections: sections ?? []
      },
      attributesToInherit: projectSettings
    });

    if (!newLevel) {
      console.error('Unable to create new level');
      errorModal(`Unable to create new level`);
      return null;
    }

    dispatch(
      setStructure(
        produce(structure, (draft) => {
          if (newLevel.type === 'basement') {
            draft.unshift(newLevel);
          } else if (newLevel.type === 'crawlspace') {
            const basements = draft.filter((l) => l.type === 'basement');
            draft.splice(basements.length, 0, newLevel);
          } else if (newLevel.type === 'level') {
            const nonAtticLevels = draft.filter((l) => l.type !== 'attic');
            draft.splice(nonAtticLevels.length, 0, newLevel);
          } else {
            draft.push(newLevel);
          }
        })
      )
    );

    if (levelType === 'crawlspace') {
      const ceilingHeightAttr = newLevel.attributes.find(
        (a) => a.code === 'ceiling_height'
      );
      if (ceilingHeightAttr) {
        dispatch(
          updateStructureItemAttribute({
            uniqueId: newLevel.uniqueId,
            attributeId: ceilingHeightAttr.id,
            value: '4'
          })
        );
      }
    }

    return newLevel;
  }
);

function structureChangeRequiresConfirmation(state: RootState): boolean {
  const { configurations } = state.configurations;
  const { selectedSystems } = state.systems;

  return structureEditResetsDesigns(configurations, selectedSystems);
}

const STRUCTURE_BASICS_RESET_MESSAGE =
  'Are you sure? This will reset all configurations and selected systems!';

export const setStructureLevelCount = createAsyncThunk<void, number>(
  'structure/setStructureLevelCount',
  async (count, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { structure } = state.structure;
    const levelTypeLevels = structure.filter((level) => level.type === 'level');
    const currentCount = levelTypeLevels.length;
    const targetCount = Math.max(0, Math.floor(count));

    if (targetCount === currentCount) {
      return;
    }

    const removedLevels =
      targetCount < currentCount ? levelTypeLevels.slice(targetCount) : [];

    const apply = () => {
      if (targetCount > currentCount) {
        for (let i = currentCount; i < targetCount; i += 1) {
          dispatch(addNewLevelToStructure({ levelType: 'level' }));
        }
      } else {
        removedLevels.forEach((level) => {
          dispatch(deleteStructureItem({ uniqueId: level.uniqueId }));
        });
      }
      dispatch(normalizeLevelOrder());
    };

    if (structureChangeRequiresConfirmation(state)) {
      confirmationModal(STRUCTURE_BASICS_RESET_MESSAGE, apply);
    } else {
      apply();
    }
  }
);

export const setStructureFoundation = createAsyncThunk<
  void,
  FoundationTypeType
>(
  'structure/setStructureFoundation',
  async (foundation, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { structure } = state.structure;
    const currentFoundation = getFoundationType(structure);

    if (foundation === currentFoundation) {
      return;
    }

    const removedLevels = structure.filter(
      (level) => level.type === 'basement' || level.type === 'crawlspace'
    );

    const apply = () => {
      removedLevels.forEach((level) => {
        dispatch(deleteStructureItem({ uniqueId: level.uniqueId }));
      });
      if (foundation !== 'none') {
        dispatch(addNewLevelToStructure({ levelType: foundation }));
      }
      dispatch(normalizeLevelOrder());
    };

    if (structureChangeRequiresConfirmation(state)) {
      confirmationModal(STRUCTURE_BASICS_RESET_MESSAGE, apply);
    } else {
      apply();
    }
  }
);

export const setStructureHasAttic = createAsyncThunk<void, boolean>(
  'structure/setStructureHasAttic',
  async (hasAttic, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { structure } = state.structure;
    const atticLevels = structure.filter((level) => level.type === 'attic');
    const currentlyHasAttic = atticLevels.length > 0;

    if (hasAttic === currentlyHasAttic) {
      return;
    }

    const apply = () => {
      if (hasAttic) {
        dispatch(addNewLevelToStructure({ levelType: 'attic' }));
      } else {
        atticLevels.forEach((level) => {
          dispatch(deleteStructureItem({ uniqueId: level.uniqueId }));
        });
      }
      dispatch(normalizeLevelOrder());
    };

    if (structureChangeRequiresConfirmation(state)) {
      confirmationModal(STRUCTURE_BASICS_RESET_MESSAGE, apply);
    } else {
      apply();
    }
  }
);

export const autoMirrorOpenSpacesToUnconditionedLevels = createAsyncThunk(
  'structure/autoMirrorOpenSpacesToUnconditionedLevels',
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { structure, footprint } = state.structure;
    const { projectSettings } = state.project;
    const openSpaceItemId = state.data.items.find(
      (item) => item.group === 'Space' && item.code === 'open_space'
    )?.id;

    if (!openSpaceItemId) {
      return;
    }

    const newStructure = autoMirrorUnconditionedLevelOpenSpaces({
      structure,
      projectSettings,
      openSpaceItemId,
      footprint
    });

    if (newStructure !== structure) {
      dispatch(setStructure(newStructure));
    }
  }
);

function syncAutoFilledSpacesToFootprints(
  dispatch: Dispatch<UnknownAction>,
  getState: () => RootState
) {
  const { structure, autoLayoutSpaceIds, footprint } = getState().structure;
  const catalogAttributes = getState().data.attributes;

  structure
    .filter(
      (level) =>
        collectLevelAutoLayoutSpaces(level, autoLayoutSpaceIds).length > 0
    )
    .forEach((level) => {
      applyLevelLeftoverRebuild(
        dispatch,
        getState().structure.structure,
        level.uniqueId,
        autoLayoutSpaceIds,
        catalogAttributes,
        footprint
      );
    });
}

export const autoGenerateHybridFullLevelSpacesThunk = createAsyncThunk(
  'structure/autoGenerateHybridFullLevelSpaces',
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { structure, footprint } = state.structure;
    const { projectSettings } = state.project;
    const customSpaceItemId = state.data.items.find(
      (item) => item.group === 'Space' && item.code === 'custom'
    )?.id;
    const openSpaceItemId = state.data.items.find(
      (item) => item.group === 'Space' && item.code === 'open_space'
    )?.id;

    if (!customSpaceItemId || !openSpaceItemId) {
      return;
    }

    const newStructure = autoGenerateHybridFullLevelSpaces({
      structure,
      projectSettings,
      customSpaceItemId,
      openSpaceItemId,
      footprint,
      catalogAttributes: state.data.attributes,
      isHybrid: isHybridOnlyPreference()
    });

    if (newStructure === structure) {
      return;
    }

    dispatch(beginUndoBatch());
    dispatch(setStructure(newStructure));
    dispatch(
      addAutoLayoutSpaceIds(collectNewSpaceIds(structure, newStructure))
    );
    dispatch(endUndoBatch());
  }
);

export const autoGenerateFullDuctworkSpaces = createAsyncThunk(
  'structure/autoGenerateFullDuctworkSpaces',
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { structure, footprint } = state.structure;
    const { projectSettings } = state.project;
    const customSpaceItemId = state.data.items.find(
      (item) => item.group === 'Space' && item.code === 'custom'
    )?.id;

    if (!customSpaceItemId) {
      return;
    }

    const newStructure = autoGenerateFullDuctworkLevelSpaces({
      structure,
      projectSettings,
      customSpaceItemId,
      footprint
    });

    if (newStructure !== structure) {
      dispatch(setStructure(newStructure));
    }
  }
);

export const setLevelFootprintWithMirror = createAsyncThunk<
  void,
  { levelUniqueId: string } & FootprintCommitPayload
>(
  'structure/setLevelFootprintWithMirror',
  async (
    { levelUniqueId, footprint, footprintShapes },
    { getState, dispatch }
  ) => {
    const typedGetState = getState as () => RootState;
    const { structure } = typedGetState().structure;

    const recipients = getLevelsReceivingMirroredFootprint(
      structure,
      levelUniqueId
    ).filter((level) => level.uniqueId !== levelUniqueId);

    dispatch(beginUndoBatch());
    try {
      dispatch(
        setLevelFootprint({ levelUniqueId, footprint, footprintShapes })
      );
      recipients.forEach((level) => {
        dispatch(
          setLevelFootprint({
            levelUniqueId: level.uniqueId,
            footprint,
            footprintShapes: footprintShapes
              ? cloneFootprintShapes(footprintShapes)
              : footprintShapes
          })
        );
      });
      syncAutoFilledSpacesToFootprints(dispatch, typedGetState);
    } finally {
      dispatch(endUndoBatch());
    }
  }
);

export const scaleAllSpacesWithImage = createAsyncThunk<
  void,
  {
    ratio: number;
    center: PositionType;
    dx: number;
    dy: number;
  }
>(
  'structure/scaleAllSpacesWithImage',
  async ({ ratio, center, dx, dy }, { getState, dispatch }) => {
    if (ratio === 1 && dx === 0 && dy === 0) return;

    const state = getState() as RootState;
    const { structure } = state.structure;

    let didChange = false;
    const newStructure = produce(structure, (draft) => {
      draft.forEach((level) => {
        level.sections.forEach((section) => {
          section.spaces.forEach((space) => {
            const scaled = scaleSpaceGeometry(space, ratio, center, dx, dy);
            if (!scaled) return;

            space.position = scaled.position;
            space.vertices = scaled.vertices;
            space.width = scaled.width;
            space.length = scaled.length;
            space.modified = true;

            const sqftAttr = space.attributes.find(
              (a) => a.code === 'square_footage'
            );
            if (sqftAttr) {
              sqftAttr.value = computeSpaceScaledSquareFootage(scaled.vertices);
            }

            didChange = true;
          });
        });
      });
    });

    if (didChange) {
      dispatch(setStructure(newStructure));
    }
  }
);

export const scaleAllLevelFootprints = createAsyncThunk<
  void,
  { ratio: number; center: PositionType; dx?: number; dy?: number }
>(
  'structure/scaleAllLevelFootprints',
  async ({ ratio, center, dx = 0, dy = 0 }, { getState, dispatch }) => {
    if (ratio === 1 && dx === 0 && dy === 0) return;

    const state = getState() as RootState;
    const { structure } = state.structure;

    const transformPoints = (points: PositionType[]) =>
      scaleFootprintPoints(points, ratio, center).map((p) => ({
        x: Math.round(p.x + dx),
        y: Math.round(p.y + dy)
      }));

    let didChange = false;
    const newStructure = produce(structure, (draft) => {
      draft.forEach((level) => {
        const shapes = getEffectiveFootprintShapes(level);
        if (shapes.length === 0) return;
        Object.assign(
          level,
          payloadFromEditedShapes(
            shapes.map((shape) => ({
              ...shape,
              points: transformPoints(shape.points)
            }))
          )
        );
        didChange = true;
      });
    });

    if (didChange) {
      dispatch(setStructure(newStructure));
    }
  }
);

export const addNewSectionToLevel = createAsyncThunk<
  SectionType | null,
  { levelId: string; spaces?: SpaceType[] }
>(
  'structure/addSectionToLevel',
  async ({ levelId, spaces }, { getState, dispatch }) => {
    abortCurrentAIRequest();
    const state = getState() as RootState;
    const { structure } = state.structure;
    const level = structure.find((l) => l.uniqueId === levelId);

    if (!level) {
      console.error('Level not found');
      errorModal(`Level not found`);
      return null;
    }

    const newSection = createSection({
      spaces: spaces ?? []
    });

    const newStructure = produce(structure, (draft) => {
      const levelIndex = draft.findIndex((l) => l.uniqueId === levelId);
      draft[levelIndex].sections.push(newSection);
    });

    dispatch(setStructure(newStructure));

    return newSection;
  }
);

export const addNewSpaceToSection = createAsyncThunk<
  SpaceType | null,
  { sectionId: string; spaceItemId: number }
>(
  'structure/addSpaceToSection',
  async ({ sectionId, spaceItemId }, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { projectSettings } = state.project;
    const { structure } = state.structure;
    const level = structure.find((l) =>
      l.sections.some((s) => s.uniqueId === sectionId)
    );

    if (!level) {
      console.error('Level not found');
      errorModal(`Level not found`);
      return null;
    }

    const newSpace = createSpace(spaceItemId, {
      attributesToInherit: [...projectSettings, ...level.attributes],
      data: { conditioned: isLevelConditioned(level) }
    });

    if (!newSpace) {
      return null;
    }

    const newStructure = produce(structure, (draft) => {
      const levelIndex = draft.findIndex((l) => l.uniqueId === level.uniqueId);
      const sectionIndex = draft[levelIndex].sections.findIndex(
        (s) => s.uniqueId === sectionId
      );
      draft[levelIndex].sections[sectionIndex].spaces.push(newSpace);
    });

    dispatch(setStructure(newStructure));
    return newSpace;
  }
);

export const addNewSpaceToLevel = createAsyncThunk<
  SpaceType | null,
  { levelId: string; spaceItemId: number }
>(
  'structure/addSpaceToLevel',
  async ({ levelId, spaceItemId }, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { structure } = state.structure;
    const { projectSettings } = state.project;
    const level = structure.find((l) => l.uniqueId === levelId);

    if (!level) {
      console.error('Level not found');
      errorModal(`Level not found`);
      return null;
    }

    const newSpace = createSpace(spaceItemId, {
      attributesToInherit: [...projectSettings, ...level.attributes],
      data: { conditioned: isLevelConditioned(level) }
    });

    if (!newSpace) {
      return null;
    }

    await dispatch(
      addNewSectionToLevel({ levelId: level.uniqueId, spaces: [newSpace] })
    );

    return newSpace;
  }
);

export const addNewSpaceToStructure = createAsyncThunk<
  SpaceType | null,
  { spaceItemId: number }
>(
  'structure/addSpaceToStructure',
  async ({ spaceItemId }, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { structure, autoLayoutSpaceIds } = state.structure;
    const { projectSettings } = state.project;
    const { activeLevelIndex } = state.ui;

    const targetLevel = structure[activeLevelIndex] ?? null;

    const conditioned = isLevelConditioned(targetLevel);

    const startX = 16;
    const startY = 16;

    const spaceWidth = 10;
    const spaceLength = 6;

    let fitted: PositionAndDimensionsType | null = null;
    let effectiveFootprints: FootprintPolygonsType = [];
    const otherSpacePolygons: PositionType[][] = [];
    let incoming: PositionAndDimensionsType = {
      x: startX,
      y: startY,
      width: spaceWidth,
      height: spaceLength
    };
    let leftoverPlans: ReturnType<typeof planLeftoverCarves> | null = null;

    if (targetLevel) {
      const leftoverTargets = leftoverRebuildTargetsForLevel(
        targetLevel,
        autoLayoutSpaceIds,
        []
      );

      const otherSpaceRects: PositionAndDimensionsType[] = [];
      for (const space of targetLevel.sections.flatMap(
        (section) => section.spaces
      )) {
        const rect = getSpaceOverlapRect(space);
        if (rect && space.position) {
          otherSpaceRects.push(rect);
          otherSpacePolygons.push(
            getSpaceVertices(space).map((v) => ({
              x: space.position!.x + v.x,
              y: space.position!.y + v.y
            }))
          );
        }
      }

      effectiveFootprints = isDetachedSpaceItem(spaceItemId)
        ? []
        : getEffectiveFootprintPolygons(targetLevel);

      incoming = clampRectToFootprints(incoming, effectiveFootprints);

      if (leftoverTargets) {
        leftoverPlans = planLeftoverCarves(leftoverTargets.leftovers, incoming);
        if (!leftoverPlans.ok && leftoverPlans.reason === 'hole') {
          warningModal(HYBRID_LEFTOVER_HOLE_MESSAGE);
          return null;
        }
      }

      if (!leftoverPlans?.ok) {
        fitted = fitSpaceAmongOthers(
          incoming,
          otherSpaceRects,
          undefined,
          otherSpacePolygons,
          effectiveFootprints
        );

        if (!fitted) {
          warningModal(
            'There is no room on the footprint for any more spaces. Only detached spaces can be added.'
          );
          return null;
        }
      }
    }

    const newSpace = createSpace(spaceItemId, {
      attributesToInherit: targetLevel
        ? [...projectSettings, ...targetLevel.attributes]
        : [...projectSettings],
      data: {
        conditioned,
        position: {
          x: leftoverPlans?.ok ? incoming.x : fitted?.x ?? startX,
          y: leftoverPlans?.ok ? incoming.y : fitted?.y ?? startY
        },
        width: spaceWidth,
        length: spaceLength
      }
    });

    if (!newSpace) {
      console.error('Unable to create new space');
      errorModal(`Unable to create new space`);
      return null;
    }

    if (targetLevel) {
      await dispatch(
        addNewSectionToLevel({
          levelId: targetLevel.uniqueId,
          spaces: [newSpace]
        })
      );

      if (leftoverPlans?.ok) {
        dispatch(beginUndoBatch());
        dispatch(
          updateStructureItem({
            uniqueId: newSpace.uniqueId,
            ...incomingSpacePatch(incoming)
          } as SpaceType)
        );
        dispatch(
          updateStructureItemAttribute({
            uniqueId: newSpace.uniqueId,
            attributeCode: 'square_footage',
            value: Math.ceil(incoming.width * incoming.height)
          })
        );
        applyLeftoverCarvePlans(
          dispatch,
          (getState() as RootState).structure.structure,
          targetLevel.uniqueId,
          leftoverPlans.plans,
          (getState() as RootState).data.attributes
        );
        dispatch(endUndoBatch());
      } else if (fitted) {
        const shapeUpdate = buildSpaceShapeWithFootprintExtensionFromPolygons({
          fitted,
          footprintPolygons: effectiveFootprints,
          otherPolygons: otherSpacePolygons,
          forceWrite: true
        });

        dispatch(beginUndoBatch());
        dispatch(
          updateStructureItem({
            uniqueId: newSpace.uniqueId,
            position: {
              x: shapeUpdate.position.x,
              y: shapeUpdate.position.y
            },
            width: shapeUpdate.width,
            length: shapeUpdate.length,
            vertices: shapeUpdate.vertices
          } as SpaceType)
        );
        dispatch(
          updateStructureItemAttribute({
            uniqueId: newSpace.uniqueId,
            attributeCode: 'square_footage',
            value: Math.ceil(polygonArea(shapeUpdate.vertices ?? []))
          })
        );
        dispatch(
          setSpaceExtensionRecords({
            spaceId: newSpace.uniqueId,
            records: shapeUpdate.extendedEdges
          })
        );
        dispatch(endUndoBatch());
      }
    } else {
      const newSection = createSection({
        spaces: [newSpace]
      });

      await dispatch(
        addNewLevelToStructure({
          levelType: 'level',
          sections: [newSection]
        })
      );
    }

    return newSpace;
  }
);

export const moveSpaceToStructure = createAsyncThunk<void, { spaceId: string }>(
  'structure/moveSpaceToStructure',
  async ({ spaceId }, { getState, dispatch }) => {
    abortCurrentAIRequest();
    const state = getState() as RootState;
    const { structure } = state.structure;

    const levelIndex = structure.findIndex((l) =>
      l.sections.some((s) => s.spaces.some((ss) => ss.uniqueId === spaceId))
    );

    if (levelIndex === -1) {
      console.error('Level not found');
      errorModal(`Level not found`);
      return;
    }

    const sectionIndex = structure[levelIndex].sections.findIndex((s) =>
      s.spaces.some((ss) => ss.uniqueId === spaceId)
    );

    if (sectionIndex === -1) {
      console.error('Section not found');
      errorModal(`Section not found`);
      return;
    }

    const spaceIndex = structure[levelIndex].sections[
      sectionIndex
    ].spaces.findIndex((s) => s.uniqueId === spaceId);

    if (spaceIndex === -1) {
      console.error('Space not found');
      errorModal(`Space not found`);
      return;
    }

    const spaceToMove =
      structure[levelIndex].sections[sectionIndex].spaces[spaceIndex];
    const newSection = createSection({
      spaces: [spaceToMove]
    });

    dispatch(deleteStructureItem({ itemId: spaceToMove.itemId }));
    dispatch(
      addNewLevelToStructure({
        levelType: 'level',
        sections: [newSection]
      })
    );
  }
);

function createRebuildLevel(
  getState: () => unknown,
  dispatch: Dispatch<UnknownAction>,
  subjectIds: readonly string[]
): (levelId: string) => void {
  return (levelId: string) => {
    const latest = getState() as RootState;
    const level = latest.structure.structure.find(
      (entry) => entry.uniqueId === levelId
    );
    if (
      !leftoverRebuildTargetsForLevel(
        level,
        latest.structure.autoLayoutSpaceIds,
        subjectIds
      )
    ) {
      return;
    }
    applyLevelLeftoverRebuild(
      dispatch,
      latest.structure.structure,
      levelId,
      latest.structure.autoLayoutSpaceIds,
      latest.data.attributes,
      latest.structure.footprint
    );
  };
}

export const deleteUserSpacesAndRebuildLeftovers = createAsyncThunk<
  void,
  string[]
>(
  'structure/deleteUserSpacesAndRebuildLeftovers',
  async (uniqueIds, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { structure, autoLayoutSpaceIds } = state.structure;
    const levelIdsToRebuild = new Set<string>();
    const rebuildLevel = createRebuildLevel(getState, dispatch, uniqueIds);

    uniqueIds.forEach((uniqueId) => {
      const level = findLevelBySpaceId(structure, uniqueId);
      if (!level) {
        return;
      }
      if (
        leftoverRebuildTargetsForLevel(level, autoLayoutSpaceIds, [uniqueId])
      ) {
        levelIdsToRebuild.add(level.uniqueId);
      }
    });

    dispatch(beginUndoBatch());
    try {
      uniqueIds.forEach((uniqueId) => {
        dispatch(deleteStructureItem({ uniqueId }));
      });

      levelIdsToRebuild.forEach(rebuildLevel);
    } finally {
      dispatch(endUndoBatch());
    }
  }
);

export const moveSpaceToLevel = createAsyncThunk<
  void,
  { spaceId: string; levelId: string }
>(
  'structure/moveSpaceToLevel',
  async ({ spaceId, levelId }, { getState, dispatch }) => {
    abortCurrentAIRequest();
    const state = getState() as RootState;
    const { structure } = state.structure;
    const fromLevelIndex = structure.findIndex((l) =>
      l.sections.some((s) => s.spaces.some((ss) => ss.uniqueId === spaceId))
    );
    const toLevelIndex = structure.findIndex((l) => l.uniqueId === levelId);

    if (fromLevelIndex === -1 || toLevelIndex === -1) {
      console.error('Level not found');
      errorModal(`Level not found`);
      return;
    }

    const fromSectionIndex = structure[fromLevelIndex].sections.findIndex((s) =>
      s.spaces.some((ss) => ss.uniqueId === spaceId)
    );

    if (fromSectionIndex === -1) {
      console.error('Section not found');
      errorModal(`Section not found`);
      return;
    }

    const fromSpaceIndex = structure[fromLevelIndex].sections[
      fromSectionIndex
    ].spaces.findIndex((s) => s.uniqueId === spaceId);

    if (fromSpaceIndex === -1) {
      console.error('Space not found');
      errorModal(`Space not found`);
      return;
    }

    const spaceToMove =
      structure[fromLevelIndex].sections[fromSectionIndex].spaces[
        fromSpaceIndex
      ];
    const movedRect = getSpaceOverlapRect(spaceToMove);
    let fittedPosition: PositionType | null = null;

    if (movedRect) {
      const targetLevel = structure[toLevelIndex];
      const otherSpaceRects: PositionAndDimensionsType[] = [];
      const otherSpacePolygons: PositionType[][] = [];
      for (const space of targetLevel.sections
        .flatMap((section) => section.spaces)
        .filter((s) => s.uniqueId !== spaceId)) {
        const rect = getSpaceOverlapRect(space);
        if (rect && space.position) {
          otherSpaceRects.push(rect);
          otherSpacePolygons.push(
            getSpaceVertices(space).map((v) => ({
              x: space.position!.x + v.x,
              y: space.position!.y + v.y
            }))
          );
        }
      }

      const effectiveFootprints = isDetachedSpaceItem(spaceToMove.itemId)
        ? []
        : getEffectiveFootprintPolygons(targetLevel);

      const fitted = fitSpaceAmongOthers(
        movedRect,
        otherSpaceRects,
        undefined,
        otherSpacePolygons,
        effectiveFootprints
      );

      if (!fitted) {
        warningModal('There is no room on that floor for this space.');
        return;
      }

      fittedPosition = { x: fitted.x, y: fitted.y };
    }

    const newStructure = produce(structure, (draft) => {
      const [movedSpace] = draft[fromLevelIndex].sections[
        fromSectionIndex
      ].spaces.splice(fromSpaceIndex, 1);

      if (fittedPosition) {
        movedSpace.position = fittedPosition;
      }

      const newSection = createSection({
        spaces: [movedSpace]
      });

      draft[toLevelIndex].sections.push(newSection);
    });

    const rebuildLevel = createRebuildLevel(getState, dispatch, [spaceId]);

    dispatch(beginUndoBatch());
    try {
      dispatch(setStructure(newStructure));
      dispatch(
        reconcileSpaceWallsRequested([{ spaceId, previousSpace: null }])
      );
      [structure[fromLevelIndex].uniqueId, levelId].forEach(rebuildLevel);
    } finally {
      dispatch(endUndoBatch());
    }
  }
);

export const moveSpaceToSection = createAsyncThunk<
  void,
  { spaceId: string; sectionId: string }
>(
  'structure/moveSpaceToSection',
  async ({ spaceId, sectionId }, { getState, dispatch }) => {
    abortCurrentAIRequest();
    const state = getState() as RootState;
    const { structure } = state.structure;

    const indices = findSpaceIndices(structure, spaceId, sectionId);
    if (!indices) {
      console.error('Space, section, or level not found');
      errorModal('Unable to move space - location not found');
      return;
    }

    const { from, toLevelIndex, toSectionIndex } = indices;

    const newStructure = produce(structure, (draft) => {
      const [spaceToMove] = draft[from.levelIndex].sections[
        from.sectionIndex
      ].spaces.splice(from.spaceIndex, 1);

      draft[toLevelIndex].sections[toSectionIndex].spaces.push(spaceToMove);
    });

    dispatch(setStructure(newStructure));
  }
);

export const moveSectionToLevel = createAsyncThunk<
  void,
  { sectionId: string; levelId: string }
>(
  'structure/moveSectionToLevel',
  async ({ sectionId, levelId }, { getState, dispatch }) => {
    abortCurrentAIRequest();
    const state = getState() as RootState;
    const { structure } = state.structure;
    const fromLevelIndex = structure.findIndex((l) =>
      l.sections.some((s) => s.uniqueId === sectionId)
    );
    const toLevelIndex = structure.findIndex((l) => l.uniqueId === levelId);

    if (fromLevelIndex !== -1 && toLevelIndex !== -1) {
      const fromSectionIndex = structure[fromLevelIndex].sections.findIndex(
        (s) => s.uniqueId === sectionId
      );

      if (fromSectionIndex !== -1) {
        const newStructure = produce(structure, (draft) => {
          const [sectionToMove] = draft[fromLevelIndex].sections.splice(
            fromSectionIndex,
            1
          );

          draft[toLevelIndex].sections.push(sectionToMove);
        });

        dispatch(setStructure(newStructure));
      } else {
        console.error('Section not found');
        errorModal(`Section not found`);
      }
    } else {
      console.error('Level not found');
      errorModal(`Level not found`);
    }
  }
);

export const moveSectionToStructure = createAsyncThunk<
  void,
  { sectionId: string }
>(
  'structure/moveSectionToStructure',
  async ({ sectionId }, { getState, dispatch }) => {
    abortCurrentAIRequest();
    const state = getState() as RootState;
    const { structure } = state.structure;

    const fromLevelIndex = structure.findIndex((l) =>
      l.sections.some((s) => s.uniqueId === sectionId)
    );

    if (fromLevelIndex !== -1) {
      const fromSectionIndex = structure[fromLevelIndex].sections.findIndex(
        (s) => s.uniqueId === sectionId
      );

      if (fromSectionIndex !== -1) {
        const newLevel = await dispatch(
          addNewLevelToStructure({ levelType: 'level' })
        ).unwrap();

        if (newLevel) {
          const newStructure = produce(structure, (draft) => {
            const toLevelIndex = draft.findIndex(
              (l) => l.uniqueId === newLevel.uniqueId
            );
            const [sectionToMove] = draft[fromLevelIndex].sections.splice(
              fromSectionIndex,
              1
            );

            draft[toLevelIndex].sections.push(sectionToMove);
          });

          dispatch(setStructure(newStructure));
        } else {
          console.error('Unable to create new level');
          errorModal(`Unable to create new level`);
        }
      } else {
        console.error('Section not found');
        errorModal(`Section not found`);
      }
    } else {
      console.error('Level not found');
      errorModal(`Level not found`);
    }
  }
);

export const reorderLevels = createAsyncThunk<
  void,
  { fromIndex: number; toIndex: number }
>(
  'structure/reorderLevels',
  async ({ fromIndex, toIndex }, { getState, dispatch }) => {
    abortCurrentAIRequest();
    const state = getState() as RootState;
    const { structure } = state.structure;

    if (
      fromIndex < 0 ||
      fromIndex >= structure.length ||
      toIndex < 0 ||
      toIndex >= structure.length ||
      fromIndex === toIndex
    ) {
      return;
    }

    const newStructure = produce(structure, (draft) => {
      const [levelToMove] = draft.splice(fromIndex, 1);
      draft.splice(toIndex, 0, levelToMove);
    });

    dispatch(setStructure(newStructure));
  }
);

export const moveSectionToSection = createAsyncThunk<
  void,
  { fromSectionId: string; toSectionId: string }
>(
  'structure/moveSectionToSection',
  async ({ fromSectionId, toSectionId }, { getState, dispatch }) => {
    abortCurrentAIRequest();
    const state = getState() as RootState;
    const { structure } = state.structure;
    const fromLevelIndex = structure.findIndex((l) =>
      l.sections.some((s) => s.uniqueId === fromSectionId)
    );
    const toLevelIndex = structure.findIndex((l) =>
      l.sections.some((s) => s.uniqueId === toSectionId)
    );

    if (fromLevelIndex !== -1 && toLevelIndex !== -1) {
      const fromSectionIndex = structure[fromLevelIndex].sections.findIndex(
        (s) => s.uniqueId === fromSectionId
      );
      const toSectionIndex = structure[toLevelIndex].sections.findIndex(
        (s) => s.uniqueId === toSectionId
      );

      if (fromSectionIndex !== -1 && toSectionIndex !== -1) {
        const sectionToMove =
          structure[fromLevelIndex].sections[fromSectionIndex];

        const newStructure = produce(structure, (draft) => {
          const spacesToMove = [...sectionToMove.spaces];

          draft[fromLevelIndex].sections.splice(fromSectionIndex, 1);

          spacesToMove.forEach((space) => {
            draft[toLevelIndex].sections[toSectionIndex].spaces.push(space);
          });
        });

        dispatch(setStructure(newStructure));
      } else {
        console.error('Section not found');
        errorModal(`Section not found`);
      }
    } else {
      console.error('Level not found');
      errorModal(`Level not found`);
    }
  }
);

export const moveSpaceToSpace = createAsyncThunk<
  void,
  {
    fromSpaceId: string;
    toSpaceId: string;
    focus?: 'levels' | 'sections' | 'spaces';
  }
>(
  'structure/moveSpaceToSpace',
  async ({ fromSpaceId, toSpaceId, focus }, { getState, dispatch }) => {
    abortCurrentAIRequest();
    const state = getState() as RootState;
    const { structure } = state.structure;
    const fromLevelIndex = structure.findIndex((l) =>
      l.sections.some((s) => s.spaces.some((ss) => ss.uniqueId === fromSpaceId))
    );
    const toLevelIndex = structure.findIndex((l) =>
      l.sections.some((s) => s.spaces.some((ss) => ss.uniqueId === toSpaceId))
    );

    if (fromLevelIndex !== -1 && toLevelIndex !== -1) {
      const fromSectionIndex = structure[fromLevelIndex].sections.findIndex(
        (s) => s.spaces.some((ss) => ss.uniqueId === fromSpaceId)
      );
      const toSectionIndex = structure[toLevelIndex].sections.findIndex((s) =>
        s.spaces.some((ss) => ss.uniqueId === toSpaceId)
      );

      if (fromSectionIndex !== -1 && toSectionIndex !== -1) {
        if (focus && focus !== 'sections') {
          dispatch(
            moveSectionToSection({
              fromSectionId:
                structure[fromLevelIndex].sections[fromSectionIndex].uniqueId,
              toSectionId:
                structure[toLevelIndex].sections[toSectionIndex].uniqueId
            })
          );
        } else {
          const fromSpaceIndex = structure[fromLevelIndex].sections[
            fromSectionIndex
          ].spaces.findIndex((s) => s.uniqueId === fromSpaceId);
          const toSpaceIndex = structure[toLevelIndex].sections[
            toSectionIndex
          ].spaces.findIndex((s) => s.uniqueId === toSpaceId);

          if (fromSpaceIndex !== -1 && toSpaceIndex !== -1) {
            const newStructure = produce(structure, (draft) => {
              const [spaceToMove] = draft[fromLevelIndex].sections[
                fromSectionIndex
              ].spaces.splice(fromSpaceIndex, 1);

              draft[toLevelIndex].sections[toSectionIndex].spaces.splice(
                toSpaceIndex,
                0,
                spaceToMove
              );
            });

            dispatch(setStructure(newStructure));
          } else {
            console.error('Space not found');
            errorModal(`Space not found`);
          }
        }
      } else {
        console.error('Section not found');
        errorModal(`Section not found`);
      }
    } else {
      console.error('Level not found');
      errorModal(`Level not found`);
    }
  }
);

export const addWindowToWall = createAsyncThunk<
  void,
  {
    spaceId: string;
    edge: EdgeType;
    windowType?: 'small' | 'medium' | 'large';
    fenestrationType?: string;
    widthOverride?: number;
    heightOverride?: number;
    positionOnWall?: number;
    run?: EdgeRunType;
  }
>(
  'structure/addWindowToWall',
  async (
    {
      spaceId,
      edge,
      windowType = 'medium',
      fenestrationType,
      widthOverride,
      heightOverride,
      positionOnWall,
      run
    },
    { getState, dispatch }
  ) => {
    abortCurrentAIRequest();
    const state = getState() as RootState;
    const { structure } = state.structure;
    const { defaultSizingItems } = state.data;
    const { projectSettings } = state.project;

    const location = findSpaceLocation(structure, spaceId);
    if (!location) {
      console.error('Space not found for adding window');
      errorModal('Space not found for adding window');
      return;
    }

    const orientationSetting = projectSettings.find(
      (s) => s.code === 'structure_orientation'
    );
    const homeOrientation = orientationSetting?.value as
      | ExposureDirectionType
      | undefined;

    const {
      levelIndex,
      sectionIndex,
      spaceIndex,
      space: sourceSpace
    } = location;
    const runLayout = findOpeningRunLayout(
      sourceSpace,
      structure[levelIndex],
      homeOrientation,
      run
    );
    const targetEdge = runLayout?.run.edge ?? edge;
    const exposure = runLayout
      ? exposureForEdgeRun(runLayout.run, homeOrientation)
      : getOrientedExposure(targetEdge, homeOrientation);
    const runBounds: OpeningRunBoundsType =
      runLayout?.run ??
      openingRunBoundsForSpaceEdge(
        sourceSpace,
        edge,
        structure[levelIndex],
        homeOrientation
      );

    const windowSize = openingPresetForWindowType(windowType);

    const newWindow = normalizeOpeningOnWall(
      withOpeningPosition(
        {
          ...defaultSizingItems.verticalGlass,
          uniqueId: nanoid(5),
          valid: true,
          modified: true,
          width: widthOverride ?? windowSize.widthFt,
          height: heightOverride ?? windowSize.heightFt,
          ...(fenestrationType && { fenestrationType })
        } as GlassItemType,
        positionOnWall
      ),
      runBounds
    );

    const isBasement = structure[levelIndex].type === 'basement';

    const newStructure = produce(structure, (draft) => {
      const space = draft[levelIndex].sections[sectionIndex].spaces[spaceIndex];
      const targetWall = runLayout
        ? findOrCreateWallForRunLayout(
            space,
            runLayout,
            exposure,
            defaultSizingItems,
            isBasement
          )
        : findOrCreateWall(
            space,
            edge,
            exposure,
            defaultSizingItems,
            isBasement
          );
      targetWall.verticalGlass = [...targetWall.verticalGlass, newWindow];
    });

    dispatch(setStructure(newStructure));
  }
);

export const separateSpaceToNewSection = createAsyncThunk<
  void,
  { spaceId: string }
>(
  'structure/separateSpaceToNewSection',
  async ({ spaceId }, { getState, dispatch }) => {
    abortCurrentAIRequest();
    const state = getState() as RootState;
    const { structure } = state.structure;

    const location = findSpaceLocation(structure, spaceId);
    if (!location) {
      console.error('Space not found for separation');
      errorModal('Space not found for separation');
      return;
    }

    const {
      space: spaceToSeparate,
      levelIndex,
      sectionIndex,
      spaceIndex
    } = location;
    const currentSection = structure[levelIndex].sections[sectionIndex];

    if (currentSection.spaces.length <= 1) {
      console.error('Space is already alone in its section');
      errorModal('Space is already alone in its section');
      return;
    }
    const newSection = createSection({
      spaces: [spaceToSeparate]
    });
    const newStructure = produce(structure, (draft) => {
      draft[levelIndex].sections[sectionIndex].spaces.splice(spaceIndex, 1);
      draft[levelIndex].sections.push(newSection);
    });

    dispatch(setStructure(newStructure));
  }
);

export const addDoorToWall = createAsyncThunk<
  void,
  {
    spaceId: string;
    edge: EdgeType;
    doorType?: 'solid' | 'pet' | 'garage';
    material?: 'Wood' | 'Metal';
    garageSize?: 'single' | 'double';
    positionOnWall?: number;
    run?: EdgeRunType;
  }
>(
  'structure/addDoorToWall',
  async (
    {
      spaceId,
      edge,
      doorType = 'solid',
      material,
      garageSize = 'single',
      positionOnWall,
      run
    },
    { getState, dispatch }
  ) => {
    abortCurrentAIRequest();
    const state = getState() as RootState;
    const { structure } = state.structure;
    const { defaultSizingItems } = state.data;
    const { projectSettings } = state.project;

    const location = findSpaceLocation(structure, spaceId);
    if (!location) {
      console.error('Space not found for adding door');
      errorModal('Space not found for adding door');
      return;
    }

    const orientationSetting = projectSettings.find(
      (s) => s.code === 'structure_orientation'
    );
    const homeOrientation = orientationSetting?.value as
      | ExposureDirectionType
      | undefined;

    const {
      levelIndex,
      sectionIndex,
      spaceIndex,
      space: sourceSpace
    } = location;
    const runLayout = findOpeningRunLayout(
      sourceSpace,
      structure[levelIndex],
      homeOrientation,
      run
    );
    const targetEdge = runLayout?.run.edge ?? edge;
    const exposure = runLayout
      ? exposureForEdgeRun(runLayout.run, homeOrientation)
      : getOrientedExposure(targetEdge, homeOrientation);
    const runBounds: OpeningRunBoundsType =
      runLayout?.run ??
      openingRunBoundsForSpaceEdge(
        sourceSpace,
        edge,
        structure[levelIndex],
        homeOrientation
      );

    const isPetDoor = doorType === 'pet';
    const isGarageDoor = doorType === 'garage';
    const doorSize = openingPresetForDoor({
      doorType,
      material,
      garageSize
    });

    const getMaterial = (): 'Wood' | 'Metal' => {
      if (material) return material;
      if (isPetDoor) return 'Wood';
      if (isGarageDoor) return 'Metal';
      return defaultSizingItems.solidDoor.material as 'Wood' | 'Metal';
    };

    const getStormMaterial = () => {
      if (isPetDoor || isGarageDoor) return 'None';
      return defaultSizingItems.solidDoor.stormMaterial;
    };

    const getCore = () => {
      if (isPetDoor) return 'Hollow';
      if (isGarageDoor) return 'Polystyrene';
      return defaultSizingItems.solidDoor.core;
    };

    const doorDraft = normalizeOpeningOnWall(
      withOpeningPosition(
        {
          ...defaultSizingItems.solidDoor,
          uniqueId: nanoid(5),
          valid: true,
          modified: true,
          width: doorSize.widthFt,
          height: doorSize.heightFt,
          material: getMaterial(),
          stormMaterial: getStormMaterial(),
          core: getCore()
        } as DoorItemType,
        positionOnWall
      ),
      runBounds
    );

    const newDoor = normalizeSolidDoorAttributes(
      doorDraft,
      state.data.validSizingAttributes.solidDoors
    ) as DoorItemType;

    const isBasement = structure[levelIndex].type === 'basement';

    const newStructure = produce(structure, (draft) => {
      const space = draft[levelIndex].sections[sectionIndex].spaces[spaceIndex];
      const targetWall = runLayout
        ? findOrCreateWallForRunLayout(
            space,
            runLayout,
            exposure,
            defaultSizingItems,
            isBasement
          )
        : findOrCreateWall(
            space,
            edge,
            exposure,
            defaultSizingItems,
            isBasement
          );
      targetWall.solidDoors = [...targetWall.solidDoors, newDoor];
    });

    dispatch(setStructure(newStructure));
  }
);

function planSpaceWallsUpdate(
  state: RootState,
  { spaceId, previousSpace }: SpaceWallReconcileRequestType
): SpaceWallsUpdateType | null {
  const { structure } = state.structure;
  const { defaultSizingItems } = state.data;

  const location = findSpaceLocation(structure, spaceId);
  if (!location) return null;

  const { space: targetSpace, levelIndex, sectionIndex, spaceIndex } = location;
  if (!targetSpace.position || !targetSpace.sizingAttributes) {
    return null;
  }

  const oldAbove = targetSpace.sizingAttributes.aboveGradeWalls;
  const oldBelow = targetSpace.sizingAttributes.belowGradeWalls;

  if (targetSpace.conditioned === false) {
    if (oldAbove.length === 0 && oldBelow.length === 0) return null;
    return {
      levelIndex,
      sectionIndex,
      spaceIndex,
      aboveGradeWalls: [],
      belowGradeWalls: []
    };
  }

  const footprints = getEffectiveFootprintPolygons(structure[levelIndex]);
  if (footprints.length === 0) {
    return null;
  }

  const orientationSetting = state.project.projectSettings.find(
    (s) => s.code === 'structure_orientation'
  );
  const homeOrientation = orientationSetting?.value as
    | ExposureDirectionType
    | undefined;

  const isBasement = structure[levelIndex].type === 'basement';
  const { ceilingHeight } = getSpaceDimensions(targetSpace);
  const belowWallIds = new Set(oldBelow.map((wall) => wall.uniqueId));
  const existingWallIds = new Set(
    [...oldAbove, ...oldBelow].map((wall) => wall.uniqueId)
  );

  const createWall = (desired: DesiredPolygonWall) => {
    const newWall = createDefaultWall(
      defaultSizingItems,
      desired.cardinal,
      desired.exposure,
      ceilingHeight,
      desired.width * desired.width,
      isBasement
    );
    newWall.width = desired.width;
    return newWall;
  };

  const reconciled = reconcileWallsForGeometry<
    AboveGradeWallType | BelowGradeWallType
  >({
    walls: [...oldAbove, ...oldBelow],
    previousGeometry: previousSpace,
    nextGeometry: targetSpace,
    footprints,
    homeOrientation,
    isBasement,
    getCardinal: (wall) => getWallCardinal(wall, homeOrientation),
    createWall,
    runBounds: openNeighborWallRunBounds(
      targetSpace,
      structure[levelIndex],
      homeOrientation
    )
  });

  const aboveGradeWalls: AboveGradeWallType[] = [];
  const belowGradeWalls: BelowGradeWallType[] = [];
  reconciled.walls.forEach((wall) => {
    const isNewWall = !existingWallIds.has(wall.uniqueId);
    const goesBelow =
      belowWallIds.has(wall.uniqueId) || (isNewWall && isBasement);
    if (goesBelow) {
      belowGradeWalls.push(wall as BelowGradeWallType);
    } else {
      aboveGradeWalls.push(wall as AboveGradeWallType);
    }
  });

  if (
    wallsContentEqual(aboveGradeWalls, oldAbove) &&
    wallsContentEqual(belowGradeWalls, oldBelow)
  ) {
    return null;
  }

  return {
    levelIndex,
    sectionIndex,
    spaceIndex,
    aboveGradeWalls,
    belowGradeWalls
  };
}

export const reconcileSpaceWalls = createAsyncThunk<
  void,
  { requests: SpaceWallReconcileRequestType[] }
>(
  'structure/reconcileSpaceWalls',
  async ({ requests }, { getState, dispatch }) => {
    const state = getState() as RootState;
    const updates = requests.flatMap((request) => {
      const update = planSpaceWallsUpdate(state, request);
      return update ? [update] : [];
    });
    if (updates.length === 0) return;

    const newStructure = produce(state.structure.structure, (draft) => {
      updates.forEach((update) => {
        const space =
          draft[update.levelIndex].sections[update.sectionIndex].spaces[
            update.spaceIndex
          ];
        if (!space.sizingAttributes) return;
        space.sizingAttributes.aboveGradeWalls = update.aboveGradeWalls;
        space.sizingAttributes.belowGradeWalls = update.belowGradeWalls;
      });
    });

    dispatch(setStructure(newStructure));
  }
);

function getAbsolutePolygonForSpace(space: SpaceType): PositionType[] | null {
  if (!space.position) return null;
  const relative = getSpaceVertices(space);
  if (relative.length < 3) return null;
  return relative.map((v) => ({
    x: space.position!.x + v.x,
    y: space.position!.y + v.y
  }));
}

export const changeSpaceShape = createAsyncThunk<
  void,
  { spaceId: string; shape: SpaceShapeId }
>(
  'structure/changeSpaceShape',
  async ({ spaceId, shape }, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { structure } = state.structure;
    const { defaultSizingItems } = state.data;

    const location = findSpaceLocation(structure, spaceId);
    if (!location) {
      errorModal('Space not found');
      return;
    }

    const { space: targetSpace, levelIndex, sectionIndex } = location;
    if (!targetSpace.position) {
      errorModal('Space has no position');
      return;
    }

    const targetAbs = getAbsolutePolygonForSpace(targetSpace);
    if (!targetAbs) {
      errorModal('Space has no geometry');
      return;
    }
    const targetBbox = boundingBoxOfVertices(targetAbs);
    const targetCenter = {
      x: targetBbox.minX + targetBbox.width / 2,
      y: targetBbox.minY + targetBbox.height / 2
    };

    const shapeVertices = getSpaceShapeLayout(shape, { x: 0, y: 0 }).vertices;
    const footprints = getEffectiveFootprintPolygons(structure[levelIndex]);
    const footprint = findPolygonForPoints(targetAbs, footprints);

    const otherSpacePolygons: PositionType[][] = [];
    structure[levelIndex].sections.forEach((section) => {
      section.spaces.forEach((other) => {
        if (other.uniqueId === targetSpace.uniqueId) return;
        const abs = getAbsolutePolygonForSpace(other);
        if (abs) otherSpacePolygons.push(abs);
      });
    });

    const templateBbox = boundingBoxOfVertices(shapeVertices);
    if (templateBbox.width <= 0 || templateBbox.height <= 0) {
      errorModal('Invalid shape template');
      return;
    }
    const scaleX = targetBbox.width / templateBbox.width;
    const scaleY = targetBbox.height / templateBbox.height;
    const scaledAbsVertices: PositionType[] = shapeVertices.map((v) => ({
      x: targetBbox.minX + (v.x - templateBbox.minX) * scaleX,
      y: targetBbox.minY + (v.y - templateBbox.minY) * scaleY
    }));

    const hasFootprint = !!footprint;
    const scaledFits =
      isPolygonInsideAnyFootprint(scaledAbsVertices, footprints) &&
      !polygonOverlapsAny(scaledAbsVertices, otherSpacePolygons);

    let finalAbsVertices: PositionType[];
    let extendedEdgeRecords: AutoExtendedEdge[] = [];

    if (scaledFits) {
      finalAbsVertices = scaledAbsVertices;
    } else {
      const fit = fitShapeInOpenArea({
        shapeVertices,
        targetCenter,
        footprint,
        otherSpacePolygons
      });
      if (!fit) {
        errorModal('The selected shape does not fit in the available space');
        return;
      }
      finalAbsVertices = fit.vertices.map((v) => ({
        x: fit.position.x + v.x,
        y: fit.position.y + v.y
      }));
    }

    if (hasFootprint) {
      const extended = extendPolygonShapeToFootprint(
        finalAbsVertices,
        footprint!,
        otherSpacePolygons
      );
      if (
        extended &&
        extended.vertices.length >= 3 &&
        isPolygonInsideFootprint(extended.vertices, footprint!) &&
        !polygonOverlapsAny(extended.vertices, otherSpacePolygons)
      ) {
        finalAbsVertices = extended.vertices;
        extendedEdgeRecords = extended.extendedEdges;
      }
    }

    if (isTriangleShape(shape)) {
      const snappedTriangle = snapTriangleVerticesToGrid(
        finalAbsVertices,
        footprint,
        otherSpacePolygons
      );
      if (snappedTriangle) {
        extendedEdgeRecords = remapExtensionRecordsAfterSnap(
          finalAbsVertices,
          snappedTriangle,
          extendedEdgeRecords
        );
        finalAbsVertices = snappedTriangle;
      }
    } else if (!isRectangleShape(shape)) {
      const snappedPolygon = snapPolygonVerticesToGrid(
        finalAbsVertices,
        footprint ?? null,
        otherSpacePolygons
      );
      if (snappedPolygon) {
        extendedEdgeRecords = remapExtensionRecordsAfterSnap(
          finalAbsVertices,
          snappedPolygon,
          extendedEdgeRecords
        );
        finalAbsVertices = snappedPolygon;
      }
    }

    const finalBbox = boundingBoxOfVertices(finalAbsVertices);
    const finalPosition = { x: finalBbox.minX, y: finalBbox.minY };
    const finalRelativeVertices: RelativeVertex[] = finalAbsVertices.map(
      (v) => ({
        x: v.x - finalPosition.x,
        y: v.y - finalPosition.y
      })
    );
    const finalArea = polygonArea(finalRelativeVertices);

    const newStructure = produce(structure, (draft) => {
      const section = draft[levelIndex].sections[sectionIndex];

      section.spaces = section.spaces.filter(
        (s) => s.uniqueId === targetSpace.uniqueId
      );

      const space = section.spaces[0];
      if (!space) {
        return;
      }

      space.position = finalPosition;
      assignSpacePolygonShape(space, finalRelativeVertices);

      const levelSpaces = draft[levelIndex].sections.flatMap(
        (levelSection) => levelSection.spaces
      );
      computeOpenToAfterReshape(space, levelSpaces).forEach((update) => {
        const updatedSpace = levelSpaces.find(
          (levelSpace) => levelSpace.uniqueId === update.uniqueId
        );
        if (updatedSpace) {
          updatedSpace.openTo = update.openTo;
        }
      });

      const squareFootageAttr = space.attributes.find(
        (attr) => attr.code === 'square_footage'
      );
      if (squareFootageAttr) {
        squareFootageAttr.value = Math.round(finalArea);
      }

      if (space.sizingAttributes && space.width && space.length) {
        const adjacency = calculateAdjacency(space, []);
        const isBasement = draft[levelIndex].type === 'basement';
        updateWallsBasedOnAdjacency(
          space,
          adjacency,
          defaultSizingItems,
          undefined,
          isBasement,
          draft[levelIndex]
        );
      }
    });

    dispatch(beginUndoBatch());
    try {
      dispatch(setStructure(newStructure));
      dispatch(
        reconcileSpaceWalls({ requests: [{ spaceId, previousSpace: null }] })
      );
      dispatch(
        setSpaceExtensionRecords({ spaceId, records: extendedEdgeRecords })
      );
    } finally {
      dispatch(endUndoBatch());
    }
  }
);

export const snapSpaceToFootprint = createAsyncThunk<void, { spaceId: string }>(
  'structure/snapSpaceToFootprint',
  async ({ spaceId }, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { structure } = state.structure;

    const location = findSpaceLocation(structure, spaceId);
    if (!location) {
      errorModal('Space not found');
      return;
    }

    const {
      levelIndex,
      sectionIndex,
      spaceIndex,
      space: targetSpace
    } = location;
    if (!targetSpace.position) {
      return;
    }

    const footprints = getEffectiveFootprintPolygons(structure[levelIndex]);
    if (footprints.length === 0) {
      errorModal('Level has no footprint defined');
      return;
    }

    const originX = targetSpace.position.x;
    const originY = targetSpace.position.y;
    const relativeVertices = getSpaceVertices(targetSpace);
    const bb = relativeVertices.reduce(
      (acc, v) => ({
        minX: Math.min(acc.minX, v.x),
        minY: Math.min(acc.minY, v.y),
        maxX: Math.max(acc.maxX, v.x),
        maxY: Math.max(acc.maxY, v.y)
      }),
      {
        minX: Infinity,
        minY: Infinity,
        maxX: -Infinity,
        maxY: -Infinity
      }
    );

    const rect = {
      x: originX + bb.minX,
      y: originY + bb.minY,
      width: bb.maxX - bb.minX,
      height: bb.maxY - bb.minY
    };
    const footprint = findContainingPolygon(rect, footprints);
    if (!footprint) {
      return;
    }

    const otherPolygons: { x: number; y: number }[][] = [];
    structure[levelIndex].sections.forEach((section) => {
      section.spaces.forEach((other) => {
        if (other.uniqueId === spaceId || !other.position) return;
        const otherRel = getSpaceVertices(other);
        otherPolygons.push(
          otherRel.map((v) => ({
            x: other.position!.x + v.x,
            y: other.position!.y + v.y
          }))
        );
      });
    });

    const extended = hasNearbyAngledFootprintSegment(rect, footprint)
      ? extendRectAsPolygonToFootprint(rect, footprint, otherPolygons) ??
        extendEdgeToFootprint(rect, footprint, otherPolygons)
      : extendEdgeToFootprint(rect, footprint, otherPolygons);
    if (!extended || extended.vertices.length < 3) {
      return;
    }

    const relative = extended.vertices.map((p) => ({
      x: p.x - originX,
      y: p.y - originY
    }));

    const newStructure = produce(structure, (draft) => {
      const space = draft[levelIndex].sections[sectionIndex].spaces[spaceIndex];
      space.vertices = relative;
    });

    dispatch(beginUndoBatch());
    try {
      dispatch(setStructure(newStructure));
      dispatch(
        reconcileSpaceWalls({ requests: [{ spaceId, previousSpace: null }] })
      );
    } finally {
      dispatch(endUndoBatch());
    }
  }
);

export const addApplianceToSpace = createAsyncThunk<
  void,
  {
    spaceId: string;
    applianceItemId: number;
  }
>(
  'structure/addApplianceToSpace',
  async ({ spaceId, applianceItemId }, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { structure } = state.structure;
    const { applianceItems, defaultSizingItems } = state.data;

    const location = findSpaceLocation(structure, spaceId);
    if (!location) {
      errorModal('Please select a space first to add appliances');
      return;
    }

    const { levelIndex, sectionIndex, spaceIndex } = location;
    const applianceItem = applianceItems.find(
      (a) => a.itemId === applianceItemId
    );
    const defaultValues = applianceItem ?? defaultSizingItems.appliance;

    const newAppliance: ApplianceType = {
      uniqueId: nanoid(5),
      itemId: applianceItemId,
      label: defaultValues?.label || 'Appliance',
      wattage: defaultValues?.wattage || 0,
      inUseOutput: defaultValues?.inUseOutput || 0,
      perHourUsage: defaultValues?.perHourUsage || 0,
      custom: false
    };

    const newStructure = produce(structure, (draft) => {
      const space = draft[levelIndex].sections[sectionIndex].spaces[spaceIndex];
      ensureSizingAttributes(space);
      space.sizingAttributes!.appliances = [
        ...(space.sizingAttributes!.appliances || []),
        newAppliance
      ];
    });

    dispatch(setStructure(newStructure));
  }
);

export const autoAddFireplaceAppliances = createAsyncThunk<void, void>(
  'structure/autoAddFireplaceAppliances',
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { structure } = state.structure;
    const { items } = state.data;

    const rawCount = getProjectSettingValue('number_of_fireplaces');
    const count = Math.floor(
      typeof rawCount === 'number' ? rawCount : Number(rawCount ?? 0)
    );
    if (!Number.isFinite(count) || count <= 0) {
      return;
    }

    if (countFireplaceAppliances(structure) > 0) {
      return;
    }

    const targetSpaceId = findFireplaceTargetSpaceId(structure, items);
    if (!targetSpaceId) {
      return;
    }

    const location = findSpaceLocation(structure, targetSpaceId);
    if (!location) {
      return;
    }

    const { levelIndex, sectionIndex, spaceIndex } = location;
    const fireplaceAppliances = Array.from({ length: count }, () =>
      createFireplaceAppliance()
    );

    const newStructure = produce(structure, (draft) => {
      const space = draft[levelIndex].sections[sectionIndex].spaces[spaceIndex];
      ensureSizingAttributes(space);
      space.sizingAttributes!.appliances = [
        ...(space.sizingAttributes!.appliances || []),
        ...fireplaceAppliances
      ];
    });

    dispatch(setStructure(newStructure));
  }
);

export const addSkylightToSpace = createAsyncThunk<
  void,
  {
    spaceId: string;
    skylightType?: 'small' | 'medium' | 'large';
  }
>(
  'structure/addSkylightToSpace',
  async ({ spaceId, skylightType = 'medium' }, { getState, dispatch }) => {
    abortCurrentAIRequest();
    const state = getState() as RootState;
    const { structure } = state.structure;
    const { defaultSizingItems } = state.data;

    const location = findSpaceLocation(structure, spaceId);
    if (!location) {
      errorModal('Space not found for adding skylight');
      return;
    }

    const { levelIndex, sectionIndex, spaceIndex } = location;
    const space =
      structure[levelIndex].sections[sectionIndex].spaces[spaceIndex];
    const ceilings = space.sizingAttributes?.ceilingsBelowRoof;

    if (!ceilings || ceilings.length === 0) {
      errorModal('This space has no ceiling below roof to add a skylight to');
      return;
    }

    const skylightSizes = {
      small: { length: 2, width: 2 },
      medium: { length: 3, width: 3 },
      large: { length: 4, width: 4 }
    };
    const skylightSize = skylightSizes[skylightType];

    const newSkylight: SkylightItemType = {
      ...defaultSizingItems.skylight,
      uniqueId: nanoid(5),
      valid: true,
      exposure: 'North',
      length: skylightSize.length,
      width: skylightSize.width
    } as SkylightItemType;

    const newStructure = produce(structure, (draft) => {
      const draftSpace =
        draft[levelIndex].sections[sectionIndex].spaces[spaceIndex];
      const targetCeiling = draftSpace.sizingAttributes!.ceilingsBelowRoof[0];
      targetCeiling.skylights = [
        ...(targetCeiling.skylights || []),
        newSkylight
      ];
    });

    dispatch(setStructure(newStructure));
  }
);

export const removeSkylightsFromSpace = createAsyncThunk<
  void,
  { spaceId: string; skylightUniqueIds: string[] }
>(
  'structure/removeSkylightsFromSpace',
  async ({ spaceId, skylightUniqueIds }, { getState, dispatch }) => {
    abortCurrentAIRequest();
    const state = getState() as RootState;
    const { structure } = state.structure;

    const location = findSpaceLocation(structure, spaceId);
    if (!location) return;

    const { levelIndex, sectionIndex, spaceIndex } = location;
    const idsToRemove = new Set(skylightUniqueIds);

    const newStructure = produce(structure, (draft) => {
      const draftSpace =
        draft[levelIndex].sections[sectionIndex].spaces[spaceIndex];
      const ceilings = draftSpace.sizingAttributes?.ceilingsBelowRoof;
      if (!ceilings) return;

      for (const ceiling of ceilings) {
        if (ceiling.skylights) {
          ceiling.skylights = ceiling.skylights.filter(
            (s) => !idsToRemove.has(s.uniqueId)
          );
        }
      }
    });

    dispatch(setStructure(newStructure));
  }
);

const FRONTEND_TO_API_KEY_MAP: Record<string, string> = {
  aboveGradeWall: 'aboveGradeWalls',
  belowGradeWall: 'belowGradeWalls',
  ceilingBelowRoofJoists: 'ceilingsBelowRoof',
  ceilingOnExposedBeams: 'ceilingsOnExposedBeams',
  ceilingBelowAttic: 'ceilingsBelowAttic',
  floorOverUnconditionedSpace: 'floorsOverUnconditionedSpace',
  floorOverExposedSpace: 'floorsOverExposedSpace',
  slabFloor: 'slabFloors',
  basementFloor: 'basementFloors',
  verticalGlass: 'verticalGlass',
  skylight: 'skylights',
  solidDoor: 'solidDoors',
  appliance: 'appliances'
};

let currentSaveDefaultsController: AbortController | null = null;

export const saveStructureSpecificSizingItemDefaults = createAsyncThunk<
  void,
  {
    structureUniqueId: string;
    updates: {
      sizingItemTypeCode: string;
      defaults: Record<string, unknown>;
    }[];
  }
>(
  'structure/saveStructureSpecificSizingItemDefaults',
  async ({ structureUniqueId, updates }, { getState }) => {
    if (currentSaveDefaultsController) {
      currentSaveDefaultsController.abort();
    }
    const controller = new AbortController();
    currentSaveDefaultsController = controller;

    try {
      const transformedUpdates = updates.map((update) => ({
        ...update,
        sizingItemTypeCode:
          FRONTEND_TO_API_KEY_MAP[update.sizingItemTypeCode] ||
          update.sizingItemTypeCode
      }));

      await axios.patch(
        'structure/sizing-item-defaults',
        { structureUniqueId, updates: transformedUpdates },
        { signal: controller.signal }
      );

      const applyResult = await axios.post<{
        itemsChecked: number;
        itemsUpdated: number;
        affectedSpaceUniqueIds: string[];
      }>(
        'structure/apply-sizing-defaults-retroactively',
        { structureUniqueId },
        { signal: controller.signal }
      );

      if (applyResult.data.affectedSpaceUniqueIds.length > 0) {
        await refetchStructureSpaces(
          applyResult.data.affectedSpaceUniqueIds,
          structureUniqueId
        );
      } else {
        const state = getState() as RootState;
        const allSpaceUniqueIds = state.structure.structure
          .flatMap((level) => level.sections)
          .flatMap((section) => section.spaces)
          .map((space) => space.uniqueId);
        await refetchStructureSpaces(allSpaceUniqueIds, structureUniqueId);
      }
    } catch (error) {
      if (controller.signal.aborted) return;
      console.error(
        'Error saving structure specific sizing item defaults:',
        error
      );
      notification('Failed to save sizing defaults', 'error');
    } finally {
      if (currentSaveDefaultsController === controller) {
        currentSaveDefaultsController = null;
      }
    }
  }
);

export const suggestSpacesForLevel = createAsyncThunk(
  'structure/suggestSpacesForLevel',
  async (
    { levelUniqueId }: { levelUniqueId: string },
    { getState, dispatch }
  ) => {
    const state = getState() as RootState;
    const { structureImageAssignments, structureAttachments } = state.structure;
    const { items } = state.data;

    const assignedImage = getAssignedImageForLevel(
      levelUniqueId,
      structureImageAssignments,
      structureAttachments
    );

    if (!assignedImage?.url) {
      dispatch(setSuggestedSpaces({ levelUniqueId, suggestedSpaces: [] }));
      return;
    }

    const spaceItems = items
      .filter((item) => item.group === 'Space')
      .map((item) => ({ code: item.code, label: item.label }));

    dispatch(setSuggestedSpaces({ levelUniqueId, suggestedSpaces: [] }));
    dispatch(setSpacesLoading(true));
    notification('Analyzing layout image for suggested spaces...', 'success');

    try {
      const response = await axios.post<{ suggestedSpaces: string[] }>(
        'ai/suggest-spaces',
        {
          imageUrl: assignedImage.url,
          spaceItems
        }
      );

      dispatch(
        setSuggestedSpaces({
          levelUniqueId,
          suggestedSpaces: response.data.suggestedSpaces ?? []
        })
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to suggest spaces for level:', error);
    } finally {
      dispatch(setSpacesLoading(false));
    }
  }
);
